import React, { useState, useEffect } from 'react'
import ReplyTile from '../ReplyTile/ReplyTile'
import { supabase } from '../../supabaseClient'
import { useDispatch, useSelector } from 'react-redux'

const ReplySection = ({ comment }) => {
  const [replies, setReplies] = useState([])
  const dispatch = useDispatch()

  const savedUsername = localStorage.getItem('currentUser')
  const reduxIsLogged = useSelector((state) => state.user.isLoggedIn)
  const isLogged = reduxIsLogged || Boolean(savedUsername)

  const rawCommentId = comment?.key ?? comment?.id ?? comment?._id
  const targetCommentId = rawCommentId !== undefined ? Number(rawCommentId) : null

  const getRepliesArray = async () => {
    if (targetCommentId === null || isNaN(targetCommentId)) return

    try {
      // 1. Fetch replies for this comment
      const { data: repliesData, error: repliesError } = await supabase
        .from('replies')
        .select('*')
        .eq('comment_id', targetCommentId)
        .order('id', { ascending: true })

      if (repliesError) throw repliesError
      if (!repliesData || repliesData.length === 0) {
        setReplies([])
        return
      }

      // 2. Collect user_ids (filtering out invalid values)
      const validUserIds = [...new Set(
        repliesData
          .map(r => r.user_id)
          .filter(id => id && id !== 'anonymous' && id !== 'undefined')
      )]

      let profileMap = {}
      if (validUserIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('user_id, username')
          .in('user_id', validUserIds)

        if (profilesData) {
          profilesData.forEach(p => {
            profileMap[p.user_id] = p.username
          })
        }
      }

      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ]

      // 3. Format replies with aggressive fallback for legacy "User" / "anonymous" rows
      const formattedReplies = repliesData.map((reply) => {
        const dateObj = new Date(reply.date || reply.created_at || Date.now())
        const dateString = `${dateObj.getDate()} ${monthNames[dateObj.getMonth()]} ${dateObj.getFullYear()}`

        let displayUsername = profileMap[reply.user_id]

        // If not in profileMap, fallback to user_id (if valid)
        if (!displayUsername && reply.user_id && reply.user_id !== 'anonymous' && reply.user_id !== 'undefined') {
          displayUsername = reply.user_id
        }

        // If reply table username column has a valid name
        if (!displayUsername && reply.username && reply.username !== 'User' && reply.username !== 'Anonymous') {
          displayUsername = reply.username
        }

        // Fallback for old legacy database rows: use current logged in user or comment username
        if (!displayUsername) {
          displayUsername = savedUsername || comment?.username || 'User'
        }

        return {
          key: reply.id,
          id: reply.id,
          username: displayUsername,
          date: dateString,
          text: reply.text,
          reply: reply.text
        }
      })

      setReplies(formattedReplies)
    } catch (e) {
      console.error('Error fetching replies:', e.message)
    }
  }

  const handleAddReply = async () => {
    const activeUsername = localStorage.getItem('currentUser')

    if (!isLogged || !activeUsername) {
      dispatch({ type: "SHOW_AUTH" })
      return
    }

    const ele = document.getElementById(`reply-input-${targetCommentId}`)
    const replyText = ele ? ele.value.trim() : ''

    if (replyText.length > 0 && targetCommentId !== null && !isNaN(targetCommentId)) {
      try {
        const { error: insertError } = await supabase
          .from('replies')
          .insert([
            {
              comment_id: targetCommentId,
              user_id: activeUsername,
              username: activeUsername,
              text: replyText
            }
          ])

        if (insertError) throw insertError

        ele.value = ''
        await getRepliesArray()
      } catch (error) {
        console.error('Error adding reply:', error.message)
        alert('Failed to add reply.')
      }
    } else {
      alert('Write a reply')
    }
  }

  useEffect(() => {
    if (comment) {
      getRepliesArray()
    }
  }, [comment])

  return (
    <div className="flex flex-col p-4 w-full pl-8">
      <div className="px-4 flex flex-col sm:flex-row">
        <input
          className="w-full my-4 sm:my-0 sm:w-1/2 max-w-96 ring-2 ring-gray-400 rounded-md h-8 mr-10 p-2 text-sm"
          required
          type="text"
          id={`reply-input-${targetCommentId}`}
          placeholder="Add a reply..."
        />
        <button
          className="bg-gray-200 w-fit h-8 ring-2 ring-gray-400 px-4 rounded-md hover:bg-gray-300 transition-colors text-sm"
          onClick={handleAddReply}
        >
          Reply
        </button>
      </div>
      <div className="px-4 w-full md:w-2/3 xl:w-1/2">
        {replies.map((reply) => (
          <ReplyTile
            key={reply.key}
            reply={reply}
          />
        ))}
      </div>
    </div>
  )
}

export default ReplySection
